import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { FiUsers, FiBriefcase, FiArrowRight, FiEdit3 } from "react-icons/fi";
import AppShell from "../components/layout/AppShell";
import PostComposer from "../components/common/PostComposer";
import PostCard from "../components/common/PostCard";
import { AuthContext } from "../context/AuthContext";
import { CommunityContext } from "../context/CommunityContext";
import { getPosts, getMyFavoritePosts } from "../api/posts";
import { getJobs } from "../api/jobs";
import { getCommunities } from "../api/communities";
import useUsersMap from "../hooks/useUsersMap";
import { Avatar, Card, Button, EmptyState, CardSkeleton, Skeleton } from "../components/ui";
import uploadUrl from "../utils/assetUrl";
import { buildFeedTimeline } from "../utils/feed";

const PAGE_SIZE = 10;

export default function Dashboard() {
  const { user } = useContext(AuthContext);
  const { joinedCommunities, loading: joinedLoading } = useContext(CommunityContext);
  const { usersById } = useUsersMap();

  const [searchParams] = useSearchParams();
  const highlightPostId = searchParams.get("post");
  const highlightRef = useRef(null);

  const [posts, setPosts] = useState([]);
  const [postsLoading, setPostsLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [favoritePostIds, setFavoritePostIds] = useState(new Set());
  const [jobs, setJobs] = useState([]);
  const [suggestedCommunities, setSuggestedCommunities] = useState([]);

  // Initial/first-page load — resets the feed back to the top. Used on
  // mount only; liking, commenting, reposting, deleting, and composing a
  // new post are all handled as surgical local-state updates instead (see
  // handleDeleted/handlePosted below), so none of them re-trigger this and
  // discard whatever additional pages the person has already loaded via
  // "Load more."
  const loadFirstPage = useCallback(async () => {
    setPostsLoading(true);
    try {
      const res = await getPosts({ limit: PAGE_SIZE });
      setPosts(res.data?.data || []);
      setHasMore(res.data?.hasMore || false);
      setNextCursor(res.data?.nextCursor || null);
    } catch (error) {
      console.error(error);
    } finally {
      setPostsLoading(false);
    }
  }, []);

  const loadMore = async () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const res = await getPosts({ limit: PAGE_SIZE, cursor: nextCursor });
      setPosts((prev) => [...prev, ...(res.data?.data || [])]);
      setHasMore(res.data?.hasMore || false);
      setNextCursor(res.data?.nextCursor || null);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingMore(false);
    }
  };

  // A new post goes straight to the top of the already-loaded list — no
  // need to re-fetch page one from the server just to get back something
  // we already have locally, plus the one new item.
  const handlePosted = (newPost) => {
    if (newPost) setPosts((prev) => [newPost, ...prev]);
  };

  // Same reasoning for deletion, in the other direction: remove just that
  // one post from local state rather than reloading everything.
  const handleDeleted = (postId) => {
    setPosts((prev) => prev.filter((p) => p.id !== postId));
  };

  useEffect(() => {
    loadFirstPage();
    getMyFavoritePosts()
      .then((res) => setFavoritePostIds(new Set(res.data?.postIds || [])))
      .catch(() => {});
    getJobs({ limit: 3 })
      .then((res) => {
        setJobs(res.data?.data || []);
      })
      .catch(() => {});
    getCommunities({ limit: 3, excludeJoined: true })
      .then((res) => {
        setSuggestedCommunities(res.data?.data || []);
      })
      .catch(() => {});
  }, [loadFirstPage]);

  const feedItems = useMemo(() => buildFeedTimeline(posts), [posts]);

  // Deep-linked from a notification (e.g. "X liked your post") — scroll to
  // and highlight the specific post once the feed has loaded.
  useEffect(() => {
    if (!postsLoading && highlightPostId && highlightRef.current) {
      highlightRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [postsLoading, highlightPostId]);

  const joinedIds = useMemo(() => new Set(joinedCommunities.map((c) => c.id)), [joinedCommunities]);
  const visibleSuggestions = useMemo(
    () => suggestedCommunities.filter((c) => !joinedIds.has(c.id)).slice(0, 3),
    [suggestedCommunities, joinedIds]
  );

  return (
    <AppShell title={`Welcome back, ${user?.name || "there"}`} subtitle="Here's what's happening in your network">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <PostComposer onPosted={handlePosted} />

          {postsLoading ? (
            <div className="space-y-4">
              <CardSkeleton />
              <CardSkeleton />
            </div>
          ) : feedItems.length === 0 ? (
            <EmptyState
              icon={<FiEdit3 className="h-6 w-6" />}
              title="No innovations shared yet"
              description="Be the first to share a project, idea, or update with the Empact community."
            />
          ) : (
            <>
              {feedItems.map((item) => {
                const isHighlighted = String(item.post.id) === highlightPostId;
                return (
                  <PostCard
                    key={item.key}
                    ref={isHighlighted ? highlightRef : undefined}
                    post={item.post}
                    author={usersById[item.post.userId]}
                    isFavorited={favoritePostIds.has(item.post.id)}
                    onDeleted={handleDeleted}
                    repostedBy={item.repostedBy}
                    highlighted={isHighlighted}
                  />
                );
              })}
              {hasMore && (
                <Button variant="secondary" onClick={loadMore} loading={loadingMore} className="w-full justify-center">
                  Load more
                </Button>
              )}
            </>
          )}
        </div>

        <div className="space-y-4">
          <Card className="p-5">
            <div className="flex items-center gap-3">
              <Avatar
                name={`${user?.name || ""} ${user?.lastName || ""}`}
                src={user?.profileImage ? uploadUrl(user.profileImage) : null}
                size="lg"
              />
              <div className="min-w-0">
                <p className="truncate font-semibold text-slate-900">
                  {user?.name} {user?.lastName}
                </p>
                <p className="truncate text-sm text-slate-500">{user?.email}</p>
              </div>
            </div>
            <Link to="/profile">
              <Button variant="secondary" size="sm" className="mt-4 w-full">
                View your profile
              </Button>
            </Link>
          </Card>

          <Card className="p-5">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-900">Your communities</h3>
              <Link to="/communities" className="text-xs font-medium text-primary-600 hover:text-primary-700">
                See all
              </Link>
            </div>
            {joinedLoading ? (
              <div className="space-y-3">
                {[1, 2].map((i) => (
                  <div key={i} className="flex items-center gap-3">
                    <Skeleton className="h-8 w-8 rounded-full" />
                    <Skeleton className="h-3 w-24" />
                  </div>
                ))}
              </div>
            ) : joinedCommunities.length === 0 ? (
              <p className="text-sm text-slate-400">You haven't joined any communities yet.</p>
            ) : (
              <ul className="space-y-3">
                {joinedCommunities.slice(0, 4).map((c) => (
                  <li key={c.id}>
                    <Link to={`/communities/${c.id}`} className="flex items-center gap-3 group">
                      <Avatar name={c.name} src={c.image ? uploadUrl(c.image) : null} size="sm" />
                      <span className="text-sm font-medium text-slate-700 group-hover:text-primary-600">
                        {c.name}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card className="p-5">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-900">Suggested communities</h3>
              <FiUsers className="h-4 w-4 text-slate-400" />
            </div>
            {visibleSuggestions.length === 0 ? (
              <p className="text-sm text-slate-400">
                {suggestedCommunities.length === 0 ? "No suggestions right now." : "You're already a member of every community."}
              </p>
            ) : (
              <ul className="space-y-3">
                {visibleSuggestions.map((c) => (
                  <li key={c.id}>
                    <Link to={`/communities/${c.id}`} className="flex items-center gap-3 group">
                      <Avatar name={c.name} src={c.image ? uploadUrl(c.image) : null} size="sm" />
                      <span className="text-sm font-medium text-slate-700 group-hover:text-primary-600">
                        {c.name}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card className="p-5">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-900">Latest jobs</h3>
              <FiBriefcase className="h-4 w-4 text-slate-400" />
            </div>
            {jobs.length === 0 ? (
              <p className="text-sm text-slate-400">No open roles right now.</p>
            ) : (
              <ul className="space-y-3">
                {jobs.map((j) => (
                  <li key={j.id} className="text-sm">
                    <p className="font-medium text-slate-700">{j.title}</p>
                    <p className="text-xs text-slate-400">{j.company}</p>
                  </li>
                ))}
              </ul>
            )}
            <Link to="/jobs">
              <Button variant="ghost" size="sm" className="mt-3 w-full">
                Browse all jobs <FiArrowRight className="h-3.5 w-3.5" />
              </Button>
            </Link>
          </Card>
        </div>
      </div>
    </AppShell>
  );
}
